/**
 * Service frontend pour les annulations de réservations
 * CRÉÉ LE: 19 août 2025
 * Selon ASSISTANT_RULES.md - Utilisation des constantes exactes
 */

import api from './api';
import { 
  CANCELLATION_MESSAGES, 
  CANCELLATION_TIME_THRESHOLDS,
  CANCELLED_BY_FRONTEND,
  TIME_CONSTANTS
} from '../constants/cancellationConstants';
import { RESERVATION_STATUS } from '../constants/statusCodes';

class CancellationService {
  
  /**
   * Vérifie si une réservation peut être annulée
   * @param {string} reservationId - ID de la réservation
   * @returns {Promise<Object>} Éligibilité à l'annulation
   */
  static async checkCancellationEligibility(reservationId) {
    try {
      const response = await api.get(`/cancellation/reservations/${reservationId}/can-cancel`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || CANCELLATION_MESSAGES.CHECK_FAILED);
      }
      
      return {
        success: true,
        data: response.data.data
      };
      
    } catch (error) {
      console.error('🚫 [CancellationService] Erreur vérification éligibilité:', error.message);
      return {
        success: false,
        error: error.message || CANCELLATION_MESSAGES.CHECK_FAILED
      };
    }
  }
  
  /**
   * Annule une réservation
   * @param {string} reservationId - ID de la réservation
   * @param {Object} cancellationData - Données d'annulation
   * @returns {Promise<Object>} Résultat de l'annulation
   */
  static async cancelReservation(reservationId, cancellationData = {}) {
    try {
      const { 
        reason = CANCELLATION_MESSAGES.USER_CANCELLATION,
        cancelled_by = CANCELLED_BY_FRONTEND.RENTER, // Par défaut RENTER pour rétrocompatibilité
        ...otherData
      } = cancellationData;
      
      const response = await api.post(`/cancellation/reservations/${reservationId}/cancel`, {
        reason,
        cancelled_by, // Utiliser la valeur fournie ou par défaut
        force_cancel: false,
        ...otherData // Inclure les autres données comme owner_notes
      });
      
      if (!response.data.success) {
        // Accept either message or error keys from the API
        const apiError = response.data.message || response.data.error || CANCELLATION_MESSAGES.CANCELLATION_FAILED;
        return {
          success: false,
          error: apiError,
          error_key: response.data.error_key || null
        };
      }

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || CANCELLATION_MESSAGES.CANCELLATION_SUCCESS
      };
      
    } catch (error) {
      console.error('🚫 [CancellationService] Erreur annulation:', error.message);
      return {
        success: false,
        error: error.message || CANCELLATION_MESSAGES.CANCELLATION_FAILED
      };
    }
  }
  
  /**
   * Récupère l'aperçu d'annulation (pénalités prévues)
   * @param {string} reservationId - ID de la réservation
   * @returns {Promise<Object>} Aperçu de l'annulation
   */
  static async getCancellationPreview(reservationId) {
    try {
      const response = await api.get(`/cancellation/reservations/${reservationId}/cancellation-preview`, {
        params: { cancelled_by: CANCELLED_BY_FRONTEND.RENTER }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || CANCELLATION_MESSAGES.PREVIEW_FAILED);
      }
      
      return {
        success: true,
        data: response.data.data
      };
      
    } catch (error) {
      console.error('🚫 [CancellationService] Erreur aperçu annulation:', error.message);
      return {
        success: false,
        error: error.message || CANCELLATION_MESSAGES.PREVIEW_FAILED
      };
    }
  }
  
  /**
   * Calcule si une réservation peut être annulée localement (calcul frontend)
   * @param {Object} reservation - Objet réservation
   * @returns {Object} Information d'annulation
   */
  static canCancelReservationLocally(reservation) {
    // Vérifier le statut de la réservation
    const cancellableStatuses = [RESERVATION_STATUS.PENDING, RESERVATION_STATUS.CONFIRMED];
    if (!cancellableStatuses.includes(reservation.status)) {
      return {
        canCancel: false,
        reason: CANCELLATION_MESSAGES.STATUS_NOT_CANCELLABLE
      };
    }

    // Vérifier si la réservation n'a pas déjà commencé
    const startDateTime = new Date(`${reservation.start_date}T00:00:00`);
    const now = new Date();
    
    if (startDateTime <= now) {
      return {
        canCancel: false,
        reason: CANCELLATION_MESSAGES.ALREADY_STARTED
      };
    }

    // Calculer les heures avant le début
    const hoursBeforeStart = Math.round((startDateTime - now) / TIME_CONSTANTS.MILLISECONDS_TO_HOURS);
    
    // Selon les règles d'annulation
    if (hoursBeforeStart < CANCELLATION_TIME_THRESHOLDS.MINIMUM_CANCELLATION_HOURS) {
      return {
        canCancel: false,
        reason: CANCELLATION_MESSAGES.TOO_CLOSE,
        hoursBeforeStart
      };
    }

    // Déterminer le type de pénalité selon les règles d'annulation
    let penaltyInfo = '';
    if (hoursBeforeStart >= CANCELLATION_TIME_THRESHOLDS.FREE_CANCELLATION_HOURS) {
      penaltyInfo = CANCELLATION_MESSAGES.FREE_CANCELLATION;
    } else {
      // Vérifier s'il y a un paiement via l'app
      const payments = reservation.payments || reservation.Payments || [];
      const hasAppPayment = payments && payments.length > 0;
      
      if (hasAppPayment) {
        penaltyInfo = CANCELLATION_MESSAGES.PENALTY_PERCENTAGE;
      } else {
        penaltyInfo = CANCELLATION_MESSAGES.REPUTATION_IMPACT;
      }
    }

    return {
      canCancel: true,
      hoursBeforeStart,
      penaltyInfo,
      reason: penaltyInfo
    };
  }
}

export default CancellationService;
